// Filter der Opportunity-Seiten als Pillen in der Bedienleiste: Pipeline, Zeitraum (Abschlussdatum oder angelegt), Leads
// (Smart View), Nutzer und, in der Liste, Status. Wirkt über die Adresse (`zustand.ts`).
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Pipeline } from "../api";
import { Auswahl, type Option } from "../components/Auswahl";
import { Ausklapp } from "../components/Ausklapp";
import { Icon, type IconName } from "../components/Icon";
import { useNutzer } from "../auth/AuthContext";
import { useMeta } from "../daten/meta";
import { heute } from "../werkzeuge/zeit";
import { ZEITRAUM_WAHLEN, wahlFuer, zeitraumFuer, zeitraumText, type Zeitraum } from "./logik";
import type { FilterWerte } from "./zustand";

function Pille({ icon, text, an, kurz }: { icon: IconName; text: ReactNode; an: boolean; kurz?: string }) {
  return (
    <>
      <Icon name={icon} groesse={14} />
      <span className="opp-pille__text">
        {kurz ? <span className="opp-pille__kurz">{kurz} </span> : null}
        {text}
      </span>
      <Icon name="chevron-unten" groesse={12} className={an ? "" : "opp-pille__pfeil"} />
    </>
  );
}

const pilleKlasse = (an: boolean) => `filterchip filterchip--auswahl opp-pille${an ? " filterchip--an" : ""}`;

// ---------------------------------------------------------------------------
// Zeitraum
// ---------------------------------------------------------------------------

interface ZeitraumProps {
  feld: "close" | "created";
  zeitraum: Zeitraum;
  onAendern: (feld: "close" | "created", zeitraum: Zeitraum) => void;
}

const GRUPPEN: { id: "vorher" | "jetzt" | "nachher"; titel: string }[] = [
  { id: "vorher", titel: "Davor" },
  { id: "jetzt", titel: "Aktuell" },
  { id: "nachher", titel: "Danach" },
];

export function ZeitraumKnopf({ feld, zeitraum, onAendern }: ZeitraumProps) {
  const [offen, setOffen] = useState(false);
  const [knopf, setKnopf] = useState<HTMLButtonElement | null>(null);
  const tag = heute();
  const an = !!(zeitraum.von || zeitraum.bis);
  const aktuell = wahlFuer(zeitraum, tag);
  const [von, setVon] = useState(zeitraum.von ?? "");
  const [bis, setBis] = useState(zeitraum.bis ?? "");
  const vonRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setVon(zeitraum.von ?? "");
    setBis(zeitraum.bis ?? "");
  }, [zeitraum.von, zeitraum.bis, offen]);

  const eigener = (v: string, b: string) => {
    if (v && b && v > b) return; // von nach bis: abwarten, bis die Eingabe stimmt
    onAendern(feld, { von: v || null, bis: b || null });
  };

  return (
    <>
      <button
        ref={setKnopf}
        type="button"
        className={pilleKlasse(an)}
        aria-haspopup="dialog"
        aria-expanded={offen}
        title="Nach Zeitraum filtern"
        onClick={() => setOffen((o) => !o)}
      >
        <Pille icon="kalender" an={an} kurz={feld === "close" ? "Abschluss:" : "Angelegt:"} text={zeitraumText(zeitraum, tag)} />
      </button>
      <Ausklapp anker={knopf} offen={offen} onSchliessen={() => setOffen(false)} breite={440} rolle="dialog" maxHoehe={520}>
        <div className="opp-zeitraum">
          <div className="opp-zeitraum__feld">
            <div className="segment" role="group" aria-label="Zeitraum bezieht sich auf">
              <button type="button" className={`segment__knopf${feld === "close" ? " segment__knopf--an" : ""}`} onClick={() => onAendern("close", zeitraum)}>
                Abschlussdatum
              </button>
              <button type="button" className={`segment__knopf${feld === "created" ? " segment__knopf--an" : ""}`} onClick={() => onAendern("created", zeitraum)}>
                Angelegt
              </button>
            </div>
          </div>
          <div className="opp-zeitraum__spalten">
            {GRUPPEN.map((g) => (
              <div key={g.id} className="opp-zeitraum__spalte">
                <div className="opp-zeitraum__titel">{g.titel}</div>
                {ZEITRAUM_WAHLEN.filter((w) => w.gruppe === g.id).map((w) => (
                  <button
                    key={w.key}
                    type="button"
                    className={`opp-zeitraum__wahl${aktuell === w.key ? " opp-zeitraum__wahl--an" : ""}`}
                    onClick={() => {
                      onAendern(feld, zeitraumFuer(w.key, tag) ?? zeitraum);
                      setOffen(false);
                    }}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div className="opp-zeitraum__offen">
            {ZEITRAUM_WAHLEN.filter((w) => w.gruppe === "offen").map((w) => (
              <button
                key={w.key}
                type="button"
                className={`opp-zeitraum__wahl${aktuell === w.key ? " opp-zeitraum__wahl--an" : ""}`}
                onClick={() => {
                  onAendern(feld, zeitraumFuer(w.key, tag) ?? zeitraum);
                  setOffen(false);
                }}
              >
                {w.label}
              </button>
            ))}
          </div>
          <div className="opp-zeitraum__eigen">
            <label className="feld">
              <span className="feld__label">Von</span>
              <input
                ref={vonRef}
                type="date"
                className="eingabe eingabe--klein"
                value={von}
                onChange={(e) => {
                  setVon(e.target.value);
                  eigener(e.target.value, bis);
                }}
              />
            </label>
            <label className="feld">
              <span className="feld__label">Bis</span>
              <input
                type="date"
                className="eingabe eingabe--klein"
                value={bis}
                onChange={(e) => {
                  setBis(e.target.value);
                  eigener(von, e.target.value);
                }}
              />
            </label>
            <button
              type="button"
              className="knopf knopf--klein knopf--leise"
              disabled={!an}
              onClick={() => {
                onAendern(feld, { von: null, bis: null });
                setOffen(false);
              }}
            >
              Alle Zeit
            </button>
          </div>
        </div>
      </Ausklapp>
    </>
  );
}

// ---------------------------------------------------------------------------
// Leiste
// ---------------------------------------------------------------------------

interface Props {
  werte: FilterWerte;
  pipelines: Pipeline[];
  /** gewählte Pipeline (auch wenn sie nur der Standard ist) */
  pipelineId: string | null;
  onAendern: (teil: Partial<FilterWerte>) => void;
  onZuruecksetzen: () => void;
  aktiv: boolean;
  /** nur Liste: Status-Auswahl zeigen */
  mitStatus?: boolean;
}

export function FilterLeiste({ werte, pipelines, pipelineId, onAendern, onZuruecksetzen, aktiv, mitStatus = false }: Props) {
  const { meta, nutzerListe } = useMeta();
  const ich = useNutzer();

  const pipelineOptionen: Option[] = pipelines.map((p) => ({ wert: p.id, label: p.name }));
  const pipeline = pipelines.find((p) => p.id === pipelineId) ?? pipelines[0];
  const statusOptionen: Option[] = useMemo(
    () =>
      (pipeline ? [pipeline] : pipelines).flatMap((p) =>
        p.statuses.map((s) => ({ wert: s.id, label: pipelines.length > 1 && !pipeline ? `${p.name}: ${s.label}` : s.label })),
      ),
    [pipeline, pipelines],
  );
  const viewOptionen: Option[] = (meta?.smart_views ?? []).filter((v) => v.type === "lead").map((v) => ({ wert: v.id, label: v.name }));
  const nutzerOptionen: Option[] = [
    { wert: "me", label: "Ich", unter: ich.name },
    ...nutzerListe.filter((u) => u.is_active && u.id !== ich.id && u.close_user_id !== ich.close_user_id).map((u) => ({ wert: u.id, label: u.name })),
  ];
  const nutzerName =
    werte.nutzer === "me"
      ? "Ich"
      : werte.nutzer
        ? nutzerListe.find((u) => u.id === werte.nutzer || u.close_user_id === werte.nutzer)?.name ?? "Nutzer"
        : "Alle Nutzer";
  const viewName = werte.view ? viewOptionen.find((v) => v.wert === werte.view)?.label ?? "Smart View" : "Alle Leads";

  return (
    <div className="opp-filter" role="group" aria-label="Filter">
      <Auswahl
        optionen={pipelineOptionen}
        wert={pipeline?.id ?? null}
        onWahl={(w) => onAendern({ pipeline: (w as string) || null, status: [] })}
        titel="Nach Pipeline filtern"
        knopfKlasse={pilleKlasse(false)}
        knopfInhalt={<Pille icon="status" an={false} text={pipeline?.name ?? "Pipeline"} />}
        breite={220}
      />
      <ZeitraumKnopf feld={werte.feld} zeitraum={werte.zeitraum} onAendern={(feld, zeitraum) => onAendern({ feld, zeitraum })} />
      <Auswahl
        optionen={viewOptionen}
        wert={werte.view}
        onWahl={(w) => onAendern({ view: (w as string | null) || null })}
        leerErlaubt
        titel="Nur Opportunities der Leads einer Smart View"
        knopfKlasse={pilleKlasse(!!werte.view)}
        knopfInhalt={<Pille icon="ansicht" an={!!werte.view} text={viewName} />}
        breite={300}
      />
      <Auswahl
        optionen={nutzerOptionen}
        wert={werte.nutzer}
        onWahl={(w) => onAendern({ nutzer: (w as string | null) || null })}
        leerErlaubt
        titel="Nach Zuständigem filtern"
        knopfKlasse={pilleKlasse(!!werte.nutzer)}
        knopfInhalt={<Pille icon="person" an={!!werte.nutzer} text={nutzerName} />}
        breite={240}
      />
      {mitStatus ? (
        <Auswahl
          optionen={statusOptionen}
          wert={werte.status}
          mehrfach
          leerErlaubt
          onWahl={(w) => onAendern({ status: w as string[] })}
          titel="Nach Status filtern"
          knopfKlasse={pilleKlasse(werte.status.length > 0)}
          knopfInhalt={
            <Pille
              icon="filter"
              an={werte.status.length > 0}
              text={
                werte.status.length === 0
                  ? "Alle Status"
                  : werte.status.length === 1
                    ? statusOptionen.find((s) => s.wert === werte.status[0])?.label ?? "1 Status"
                    : `${werte.status.length} Status`
              }
            />
          }
          breite={260}
        />
      ) : null}
      {aktiv ? (
        <button type="button" className="knopf knopf--klein knopf--leise opp-filter__weg" onClick={onZuruecksetzen} title="Alle Filter außer der Pipeline zurücksetzen">
          <Icon name="schliessen" groesse={12} />
          Zurücksetzen
        </button>
      ) : null}
    </div>
  );
}
